import { api } from "@breakout/backend/convex/_generated/api"
import { secToMs } from "@breakout/shared/utils/time"
import { NumberTicker } from "@breakout/ui/components/NumberTicker"
import { useToast } from "@breakout/ui/components/Toast"
import { cn, formatCompactUsd } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useFundWallet, usePrivy, useWallets } from "@privy-io/react-auth"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useLocation } from "@tanstack/react-router"
import { zodSearchValidator } from "@tanstack/router-zod-adapter"
import { ArrowLeft, ExternalLink } from "lucide-react"
import { motion } from "motion/react"
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { z } from "zod"
import { PageContainer } from "../../components/layout/PageContainer"
import { ActivityFeed } from "../../components/market/ActivityFeed"
import { MarketDescription } from "../../components/market/MarketDescription"
import { MarketSheet } from "../../components/market/MarketSheet"
import { OutcomeRow } from "../../components/market/OutcomeRow"
import { PriceChart } from "../../components/market/PriceChart"
import { TopHolders } from "../../components/market/TopHolders"
import { type TradeMode, TradingPanel } from "../../components/market/TradingPanel"
import { TweetsFeed } from "../../components/market/TweetsFeed"
import { ShareTradeModal } from "../../components/modals/ShareTradeModal"
import { AnimatedTabs } from "../../components/ui/AnimatedTabs"
import { CountdownBadge } from "../../components/ui/CountdownBadge"
import { SimpleAvatar } from "../../components/ui/SimpleAvatar"
import { XLogo } from "../../components/ui/XLogo"
import { useDocumentTitle } from "../../hooks/common/useDocumentTitle"
import { useTabNavigation } from "../../hooks/common/useTabNavigation"
import { useMarketStats } from "../../hooks/indexer/useMarketStats"
import { useMarketPrices } from "../../hooks/market/useMarketPrices"
import { type TradeStatus, useBuy } from "../../hooks/trading/useBuy"
import { useBuyQuote } from "../../hooks/trading/useBuyQuote"
import { useSell } from "../../hooks/trading/useSell"
import { useAuthState } from "../../hooks/user/useAuthState"
import { useUserPosition } from "../../hooks/user/useUserPosition"
import { useUsdcBalance } from "../../hooks/wallet/useUsdcBalance"
import { BINARY_LABELS, OUTCOME_DEFS_5 as OUTCOME_DEFS_5_CONST } from "../../lib/constants"
import { InsufficientFundsError } from "../../lib/errors/tradeErrors"
import { FFG_ICON_URL } from "../../lib/ffgIcon"
import { formatBucketDisplayLabel } from "../../lib/marketLabels"
import { breakoutChain } from "../../lib/providers"
import { parseTradeErrorMessage } from "../../lib/sentry"
import type { Creator, OutcomeBucket } from "../../lib/types"
import { findEmbeddedWallet } from "../../lib/wallet"

const marketSearchSchema = z.object({
	tab: z.enum(["tweets", "holders", "activity"]).optional().catch(undefined),
})

type MarketTab = "tweets" | "holders" | "activity"

type OutcomeDirection = "up" | "neutral" | "down"

interface OutcomeDefinition {
	label: string
	direction: OutcomeDirection
	minRank: number
	maxRank: number | null
	avatarUrl: string | null
}

// =============================================================================
// Constants
// =============================================================================

// Default 5-bucket outcome definitions (for multi-outcome markets)
const OUTCOME_DEFS_5: OutcomeDefinition[] = OUTCOME_DEFS_5_CONST.map(def => ({
	...def,
	minRank: def.minRank ?? 1,
	maxRank: def.maxRank ?? null,
	avatarUrl: null,
}))

// Binary market outcome definitions (2 buckets) - use centralized labels
const OUTCOME_DEFS_BINARY: OutcomeDefinition[] = [
	{ label: BINARY_LABELS[0], direction: "up", minRank: 1, maxRank: 10, avatarUrl: null },
	{ label: BINARY_LABELS[1], direction: "down", minRank: 11, maxRank: null, avatarUrl: null },
]

/** Outcome from backend with resolved avatar URL */
interface BackendOutcome {
	label: string
	avatarUrl: string // Always provided by backend (with dicebear fallback)
}

interface GetOutcomeDefsArgs {
	bucketCount: number
	outcomeLabels?: string[]
	outcomes?: BackendOutcome[] | null
	/** Market type for label formatting */
	marketType?: "binary" | "fall_from_grace"
	/** Start rank for binary markets (1-20) */
	startRank?: number | null
	/** Creator display name for formatted labels */
	creatorName?: string
	/** Creator avatar URL for binary markets */
	creatorAvatarUrl?: string
}

/**
 * Get outcome definitions based on bucket count and optional labels
 * Uses backend-resolved outcomes when available (for Fall from Grace markets)
 */
function getOutcomeDefs({
	bucketCount,
	outcomeLabels,
	outcomes,
	marketType,
	startRank,
	creatorName,
	creatorAvatarUrl,
}: GetOutcomeDefsArgs): OutcomeDefinition[] {
	// 1. Use backend-resolved outcomes (includes avatar URLs for Fall from Grace)
	if (outcomes && outcomes.length > 0) {
		return outcomes.map((outcome, index) => {
			// Format label for FFG markets
			const label =
				marketType === "fall_from_grace"
					? formatBucketDisplayLabel({
							marketType: "fall_from_grace",
							startRank: null,
							creatorName: outcome.label,
							bucketIndex: index,
							baseLabel: outcome.label,
						})
					: outcome.label
			return {
				label,
				direction: index === 0 ? "up" : index === outcomes.length - 1 ? "down" : "neutral",
				minRank: 1,
				maxRank: null,
				avatarUrl: outcome.avatarUrl,
			}
		})
	}

	// 2. Binary markets - format with context
	if (bucketCount === 2 && marketType === "binary" && creatorName) {
		return [0, 1].map(index => {
			const baseLabel = BINARY_LABELS[index] ?? (index === 0 ? "Long" : "Short")
			return {
				label: formatBucketDisplayLabel({
					marketType: "binary",
					startRank: startRank ?? null,
					creatorName,
					bucketIndex: index,
					baseLabel,
				}),
				direction: index === 0 ? "up" : "down",
				minRank: index === 0 ? 1 : 11,
				maxRank: index === 0 ? 10 : null,
				avatarUrl: creatorAvatarUrl ?? null,
			}
		})
	}

	// 3. Fallback to labels without avatars (route state before Convex loads)
	if (outcomeLabels && outcomeLabels.length > 0) {
		return outcomeLabels.map((label, index) => ({
			label:
				marketType === "fall_from_grace"
					? formatBucketDisplayLabel({
							marketType: "fall_from_grace",
							startRank: null,
							creatorName: label,
							bucketIndex: index,
							baseLabel: label,
						})
					: label,
			direction: index === 0 ? "up" : index === outcomeLabels.length - 1 ? "down" : "neutral",
			minRank: 1,
			maxRank: null,
			avatarUrl: null,
		}))
	}

	// 4. Fallback for Binary Markets (if no context provided)
	if (bucketCount === 2) {
		return OUTCOME_DEFS_BINARY
	}

	// 5. Default for 5-bucket markets
	return OUTCOME_DEFS_5
}

/** Safely convert string to Address or undefined */
function toAddress(value: string | undefined): Address | undefined {
	if (!value || !isAddress(value)) {
		return undefined
	}
	return getAddress(value)
}

/** Data passed from home page to avoid flash on navigation */
export interface MarketRouteState {
	name?: string
	handle?: string
	avatarUrl?: string
	upPercent?: number
	liquidity?: number
	rank?: number
	mindsharePct?: number
	title?: string
	outcomeLabels?: string[]
	cutoffTimeSec?: number
	openTimeSec?: number
	marketType?: "binary" | "fall_from_grace"
}

export const Route = createFileRoute("/markets/$marketId")({
	component: MarketDetailPage,
	validateSearch: zodSearchValidator(marketSearchSchema),
})

function MarketDetailPage() {
	// 1. Routing params + state from home page navigation
	const { marketId } = Route.useParams()
	const { tab } = Route.useSearch()
	const location = useLocation()
	const routeState = location.state as MarketRouteState | undefined

	// 2. Context/store access
	const { navigateWithToast } = useTabNavigation()
	const { showToast } = useToast()
	const { login } = usePrivy()
	const { isAuthenticated, evmWallet } = useAuthState()
	const { wallets } = useWallets()
	const { fundWallet } = useFundWallet()
	const buyMutation = useBuy()
	const sellMutation = useSell()

	// Local state
	const [tradeSide, setTradeSide] = useState<TradeMode>("buy")
	const [amount, setAmount] = useState(50)
	const [selectedBucketId, setSelectedBucketId] = useState<string | null>(null)
	const [isSheetOpen, setIsSheetOpen] = useState(false)
	// Share modal state
	const [shareModalOpen, setShareModalOpen] = useState(false)
	const [lastTradedOutcomeLabel, setLastTradedOutcomeLabel] = useState<string>("")
	const [lastTradedDirection, setLastTradedDirection] = useState<"up" | "neutral" | "down">(
		"neutral",
	)
	// Insufficient funds state - shows "Add cash" button
	const [showAddCash, setShowAddCash] = useState(false)

	const activeTab = tab ?? "tweets"

	// Parse market address directly from route params - this is the key to parallel fetching
	// The marketId IS the market address, so we can start fetching immediately
	const marketAddress: Address | undefined = toAddress(marketId)
	const walletAddress: Address | undefined = toAddress(evmWallet?.address)

	// Get embedded wallet address for funding (only embedded wallets can be funded via Privy)
	const embeddedWallet = findEmbeddedWallet(wallets)
	const embeddedWalletAddress: Address | null =
		embeddedWallet?.address && isAddress(embeddedWallet.address)
			? getAddress(embeddedWallet.address)
			: null

	// 3. Data queries - PARALLEL DATA FETCHING: All these queries can start immediately
	// They don't need to wait for Convex metadata
	const {
		data: prices,
		isLoading: isLoadingPrices,
		refetch,
	} = useMarketPrices(marketAddress ? { marketAddress } : null)
	const { data: userPosition, refetch: refetchShares } = useUserPosition(
		marketAddress && walletAddress ? { marketAddress, userAddress: walletAddress } : null,
	)
	// Fetch user's USDC balance for percentage-based buy amounts
	const { data: usdcBalanceData } = useUsdcBalance(walletAddress ? { walletAddress } : null)
	const { data: marketStats } = useMarketStats(marketAddress ? { marketAddress } : null)

	// Fetch market metadata from Convex (for display name, avatar, etc.)
	const {
		data: convexMarket,
		isLoading: isLoadingConvexMarket,
		isError: isConvexError,
		isFetched: isConvexFetched,
	} = useQuery(
		convexQuery(api.domains.markets.queries.getByAddress, {
			marketAddress: marketId,
		}),
	)

	const market = convexMarket
	// Skip main loading skeleton if we have route state from home page navigation
	// This provides instant content while Convex data loads in background
	const hasInitialData = !!routeState?.name
	const isLoading = isLoadingConvexMarket && !hasInitialData
	// Definitively not found: query completed but no data
	const isNotFound = isConvexFetched && !convexMarket && !isConvexError

	// Fetch week details for snapshot links
	const { data: weekDetails } = useQuery({
		...convexQuery(api.public.config.getWeekDetails, {
			weekId: market?.weekId ?? "",
		}),
		enabled: !!market?.weekId,
	})

	const shares = userPosition?.shares
	// Use raw bigint balance for precise percentage calculations
	const userBalanceRaw = usdcBalanceData?.balance ?? null

	// Extract typed fields from market (only ConvexMarket has these)
	const typedMarket = market && "marketType" in market ? market : null

	// Determine bucket count from on-chain data or market metadata (with route state fallback)
	const bucketCount =
		prices?.prices.length ??
		typedMarket?.outcomeLabels?.length ??
		routeState?.outcomeLabels?.length ??
		2

	// Get outcome labels and resolved outcomes from market (with route state fallback)
	const marketOutcomeLabels = typedMarket?.outcomeLabels ?? routeState?.outcomeLabels
	const marketOutcomes = typedMarket?.outcomes
	const marketType = typedMarket?.marketType ?? routeState?.marketType
	const isFallFromGrace = marketType === "fall_from_grace"

	// Get startRank and creator name early for outcome label formatting
	// Use route state fallback for instant display
	const startRank = typedMarket?.startRank ?? routeState?.rank ?? 1
	const shortKey = shortenKey(marketId)
	const marketName =
		market?.displayName || market?.handle || routeState?.name || `Market ${shortKey}`
	// Market title with route state fallback for instant display
	const marketTitle = typedMarket?.title ?? routeState?.title ?? marketName

	// Get creator avatar URL for binary markets (with route state fallback)
	const creatorAvatarUrl = market?.avatarUrl ?? routeState?.avatarUrl

	// 5. Derived values (useMemo)
	// Get dynamic outcome definitions based on bucket count and stored labels
	const outcomeDefs = useMemo(
		() =>
			getOutcomeDefs({
				bucketCount,
				outcomeLabels: marketOutcomeLabels ?? undefined,
				outcomes: marketOutcomes,
				marketType: marketType ?? undefined,
				startRank,
				creatorName: marketName,
				creatorAvatarUrl,
			}),
		[
			bucketCount,
			marketOutcomeLabels,
			marketOutcomes,
			marketType,
			startRank,
			marketName,
			creatorAvatarUrl,
		],
	)

	const fallbackProbabilities = useMemo(() => {
		// Use route state prices if available (from home page navigation)
		if (routeState?.upPercent !== undefined && bucketCount === 2) {
			const upProb = routeState.upPercent / 100
			return [upProb, 1 - upProb]
		}
		// Fall back to seeded random for direct navigation or multi-bucket markets
		return createSeededProbabilities(marketId || "default", bucketCount)
	}, [marketId, bucketCount, routeState?.upPercent])

	const probabilities = useMemo(
		() =>
			prices && marketAddress
				? prices.prices // Already in 0-1 range from SDK
				: fallbackProbabilities,
		[prices, marketAddress, fallbackProbabilities],
	)

	const buckets = useMemo<OutcomeBucket[]>(
		() =>
			outcomeDefs.map((bucket, index) => ({
				id: String(index),
				label: bucket.label,
				direction: bucket.direction,
				minRank: bucket.minRank,
				maxRank: bucket.maxRank,
				probability: probabilities[index] ?? 0,
				price: probabilities[index] ?? 0,
				avatarUrl: bucket.avatarUrl ?? null,
			})),
		[outcomeDefs, probabilities],
	)

	const userSharesRecord = useMemo(() => {
		const record: Record<string, number> = {}
		if (shares) {
			shares.forEach((shareAmount: number, index: number) => {
				record[String(index)] = shareAmount
			})
		}
		return record
	}, [shares])

	// Initialize selected bucket (effect for state sync)
	useEffect(() => {
		if (!selectedBucketId && buckets.length > 0) {
			setSelectedBucketId(buckets[0]?.id ?? null)
		}
	}, [buckets, selectedBucketId])

	// More derived values
	const selectedBucket = useMemo(
		() => buckets.find(bucket => bucket.id === selectedBucketId) ?? buckets[0],
		[buckets, selectedBucketId],
	)
	const selectedShares = selectedBucket ? (userSharesRecord[selectedBucket.id] ?? 0) : 0

	// Defer amount for quotes to prevent RPC calls on every keystroke
	const deferredAmount = useDeferredValue(amount)

	// Get real-time quote for estimated shares display
	const selectedBucketIndex = selectedBucket ? Number.parseInt(selectedBucket.id, 10) : 0
	const { data: buyQuote } = useBuyQuote(
		marketAddress && tradeSide === "buy" && deferredAmount > 0
			? { marketAddress, bucket: selectedBucketIndex, usdAmount: deferredAmount }
			: null,
	)

	const estimatedShares = buyQuote?.sharesOut ?? null

	// Update document title
	useDocumentTitle(market ? marketName : null)
	// Use FFG icon for Fall from Grace markets, otherwise use market avatar
	// Fallback chain: market data → route state from home page → empty (shows skeleton)
	const avatarUrl = isFallFromGrace
		? FFG_ICON_URL
		: (market?.avatarUrl ?? routeState?.avatarUrl ?? "")

	// Convert Convex timestamps (Unix seconds) to JS milliseconds
	// Use route state fallback for instant countdown display on navigation
	const marketCloseTimeMs = market?.cutoffTimeSec
		? secToMs(market.cutoffTimeSec)
		: routeState?.cutoffTimeSec
			? secToMs(routeState.cutoffTimeSec)
			: null
	const marketOpenTimeMs = market?.openTimeSec
		? secToMs(market.openTimeSec)
		: routeState?.openTimeSec
			? secToMs(routeState.openTimeSec)
			: null
	const nowMs = Date.now()
	const isMarketEnded = marketCloseTimeMs ? nowMs >= marketCloseTimeMs : false
	const isMarketOpen = marketOpenTimeMs ? nowMs >= marketOpenTimeMs : true // Default to open if no openTime
	// Only enable trading if market is loaded, open, and not ended
	const isTradingEnabled = !!market && isMarketOpen && !isMarketEnded

	// Derived trade status for UI
	const tradeStatus: TradeStatus = tradeSide === "buy" ? buyMutation.status : sellMutation.status
	const isTradeInProgress = tradeStatus === "processing"
	// Disable buttons while trade is in progress or mutation is pending
	const isPending = tradeSide === "buy" ? buyMutation.isPending : sellMutation.isPending
	const isSubmitting = isTradeInProgress || isPending
	const liquidityUsd = marketStats?.liquidityUsdc ?? routeState?.liquidity ?? null
	const volumeUsd = marketStats?.volumeUsdc ?? null

	// Get mindsharePct from typed market with route state fallback
	const mindsharePct = typedMarket?.mindsharePct ?? routeState?.mindsharePct

	// Custom question markets (handle starts with "custom-question") don't have mindshare
	const isCustomQuestion =
		market?.handle?.startsWith("custom-question") ??
		routeState?.handle?.startsWith("custom-question") ??
		false

	// For FFG markets, find the leading bucket (highest probability, lowest index on tie)
	const leadingBucket = useMemo(() => {
		if (!isFallFromGrace || buckets.length === 0) {
			return null
		}
		let best = buckets[0]
		for (let i = 1; i < buckets.length; i++) {
			const b = buckets[i]
			if (b && b.probability > (best?.probability ?? 0)) {
				best = b
			}
		}
		return best ?? null
	}, [isFallFromGrace, buckets])

	const creator: Creator = {
		id: marketId,
		name: marketName,
		handle: market?.handle || routeState?.handle || shortKey,
		avatarUrl,
		rank: routeState?.rank ?? startRank,
		mindsharePct: routeState?.mindsharePct ?? mindsharePct ?? 0,
	}

	// 6. Event handlers
	const setActiveTab = useCallback(
		(newTab: MarketTab) => {
			navigateWithToast({
				to: "/markets/$marketId",
				params: { marketId },
				search: { tab: newTab === "tweets" ? undefined : newTab },
				replace: true,
				resetScroll: false,
			})
		},
		[navigateWithToast, marketId],
	)

	// Handler to open Privy fund wallet modal
	const handleAddCash = useCallback(async () => {
		if (!embeddedWalletAddress) {
			return
		}
		try {
			await fundWallet({
				address: embeddedWalletAddress,
				options: { chain: breakoutChain }, // Explicitly fund on Base
			})
			// Reset the add cash state after funding modal closes
			setShowAddCash(false)
		} catch (error) {
			console.warn("[FundWallet] Error opening fund wallet:", error)
		}
	}, [embeddedWalletAddress, fundWallet])

	const handleBuy = useCallback(
		async (bucketId: string, amountValue: number) => {
			// Reset add cash state on new buy attempt
			setShowAddCash(false)

			if (!isMarketOpen) {
				showToast("Market hasn't opened yet", "error")
				return
			}
			if (isMarketEnded) {
				showToast("Market has ended - trading is closed", "error")
				return
			}
			if (!isAuthenticated) {
				showToast("Please log in to trade", "error")
				return
			}
			if (!marketAddress) {
				showToast("Market not ready", "error")
				return
			}

			try {
				const bucketIndex = Number.parseInt(bucketId, 10)

				// Use gasless trading - user signs, relayer pays gas
				// SDK calculates optimal shares for the USD amount
				await buyMutation.mutateAsync({
					marketAddress,
					bucket: bucketIndex,
					usdAmount: amountValue,
					slippageBps: 200, // 2% slippage
				})

				// Find the bucket label and direction for the share modal
				const tradedBucket = buckets.find(b => b.id === bucketId)
				const outcomeLabel = tradedBucket?.label ?? "this outcome"
				const direction = tradedBucket?.direction ?? "neutral"

				// Show share modal instead of toast
				setLastTradedOutcomeLabel(outcomeLabel)
				setLastTradedDirection(direction)
				setShareModalOpen(true)

				await refetch()
				await refetchShares()
			} catch (err) {
				// Check if it's an insufficient funds error - show toast and "Add cash" button
				if (err instanceof InsufficientFundsError) {
					showToast("Insufficient funds", "error")
					setShowAddCash(true)
					return
				}
				const message = parseTradeErrorMessage({ error: err, action: "buy" })
				showToast(message, "error")
				throw err
			}
		},
		[
			buckets,
			buyMutation,
			isAuthenticated,
			isMarketEnded,
			isMarketOpen,
			marketAddress,
			refetch,
			refetchShares,
			showToast,
		],
	)

	const handleSell = useCallback(
		async (bucketId: string, shareAmount: number) => {
			if (!isMarketOpen) {
				showToast("Market hasn't opened yet", "error")
				return
			}
			if (isMarketEnded) {
				showToast("Market has ended - trading is closed", "error")
				return
			}
			if (!isAuthenticated) {
				showToast("Please log in to trade", "error")
				return
			}
			if (!marketAddress) {
				showToast("Market not ready", "error")
				return
			}

			try {
				const bucketIndex = Number.parseInt(bucketId, 10)

				// Use gasless trading - user signs, relayer pays gas
				await sellMutation.mutateAsync({
					marketAddress,
					bucket: bucketIndex,
					shares: shareAmount,
					slippageBps: 200, // 2% slippage
				})

				showToast("Shares sold successfully!", "success")
				await Promise.all([refetch(), refetchShares()])
			} catch (err) {
				const message = parseTradeErrorMessage({ error: err, action: "sell" })
				showToast(message, "error")
				throw err
			}
		},
		[
			sellMutation,
			isAuthenticated,
			isMarketEnded,
			isMarketOpen,
			marketAddress,
			refetch,
			refetchShares,
			showToast,
		],
	)

	// Mobile-specific handlers (memoized to prevent OutcomeRow re-renders)
	const handleMobileBuy = useCallback((bucketId: string) => {
		setSelectedBucketId(bucketId)
		setTradeSide("buy")
		setIsSheetOpen(true)
	}, [])

	const handleMobileSell = useCallback((bucketId: string, shares: number) => {
		setSelectedBucketId(bucketId)
		setTradeSide("sell")
		setAmount(shares)
		setIsSheetOpen(true)
	}, [])

	const handleDesktopConfirm = useCallback(async () => {
		if (!selectedBucket || amount <= 0) {
			return
		}
		if (tradeSide === "sell" && amount > selectedShares) {
			showToast("Sell amount exceeds your shares", "error")
			return
		}
		if (!isAuthenticated) {
			showToast("Please log in to trade", "error")
			return
		}

		if (tradeSide === "buy") {
			await handleBuy(selectedBucket.id, amount)
		} else {
			await handleSell(selectedBucket.id, amount)
		}
	}, [
		selectedBucket,
		amount,
		tradeSide,
		selectedShares,
		showToast,
		isAuthenticated,
		handleBuy,
		handleSell,
	])

	// Get status label for button - simplified to single "Processing" state
	const getStatusLabel = (): string => {
		if (tradeStatus === "confirmed") {
			return "Confirmed!"
		}
		if (isTradeInProgress) {
			return "Processing…"
		}
		return tradeSide === "buy" ? "Buy" : "Sell"
	}

	if (isLoading) {
		return (
			<div className="overflow-x-hidden bg-bg-primary text-text-primary">
				<PageContainer className="pt-5 pb-8 sm:pt-8">
					<div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-12">
						<div className="min-w-0 space-y-8 lg:order-1">
							{/* Main header - no card wrapper */}
							<div className="px-1">
								{/* Header skeleton - matches actual: size-12 sm:size-14 avatar with shadow */}
								<div className="flex items-center gap-3 sm:gap-4">
									<div className="size-12 shrink-0 animate-pulse rounded-xl bg-bg-tertiary shadow-lg sm:size-14 sm:rounded-2xl" />
									{/* Title - matches text-lg sm:text-2xl lg:text-3xl with optional X link */}
									<div className="flex min-w-0 flex-1 items-center gap-2">
										<div className="h-6 w-40 animate-pulse rounded bg-bg-tertiary sm:h-7 sm:w-48 lg:h-9" />
									</div>
									{/* Time badge - CountdownBadge size="sm" */}
									<div className="h-[30px] w-[110px] shrink-0 animate-pulse rounded-full border border-border-subtle bg-bg-secondary sm:h-[34px] sm:w-[130px]" />
								</div>
								{/* Market question skeleton: matches mt-6 text-2xl sm:text-3xl leading-tight, typically wraps to 2 lines on mobile */}
								<div className="mt-6 space-y-2">
									<div className="h-8 w-full animate-pulse rounded-lg bg-bg-tertiary sm:h-9" />
									<div className="h-8 w-2/3 animate-pulse rounded-lg bg-bg-tertiary sm:h-9" />
								</div>
								{/* Stats - matches actual stats row layout exactly */}
								<div className="mt-6 flex items-center gap-x-5 text-base sm:gap-x-8">
									{/* Rank */}
									<div className="flex flex-col gap-0.5">
										<div className="h-4 w-10 animate-pulse rounded bg-bg-tertiary" />
										<div className="h-7 w-8 animate-pulse rounded bg-bg-tertiary" />
									</div>
									{/* Chance */}
									<div className="flex flex-col gap-0.5">
										<div className="h-4 w-12 animate-pulse rounded bg-bg-tertiary" />
										<div className="h-7 w-10 animate-pulse rounded bg-bg-tertiary" />
									</div>
									{/* Mindshare */}
									<div className="flex flex-col gap-0.5">
										<div className="h-4 w-16 animate-pulse rounded bg-bg-tertiary" />
										<div className="h-7 w-16 animate-pulse rounded bg-bg-tertiary" />
									</div>
									{/* Vol */}
									<div className="flex flex-col gap-0.5">
										<div className="h-4 w-8 animate-pulse rounded bg-bg-tertiary" />
										<div className="h-7 w-10 animate-pulse rounded bg-bg-tertiary" />
									</div>
									{/* Liq */}
									<div className="flex flex-col gap-0.5">
										<div className="h-4 w-8 animate-pulse rounded bg-bg-tertiary" />
										<div className="h-7 w-10 animate-pulse rounded bg-bg-tertiary" />
									</div>
								</div>
							</div>

							{/* Mobile Outcome Rows skeleton - matches OutcomeRow variant="compact" */}
							<div className="lg:hidden">
								<div className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
									{[0, 1].map(i => (
										<div className="flex items-center gap-2 px-3 py-2.5" key={i}>
											{/* Avatar placeholder with arrow overlay */}
											<div className="relative size-8 shrink-0">
												<div className="size-8 animate-pulse rounded-lg bg-bg-tertiary" />
												<div
													className={cn(
														"absolute -right-1 -bottom-1 size-4 rounded-full",
														i === 0 ? "bg-green-500" : "bg-red-500",
													)}
												/>
											</div>
											{/* Name - text-sm font-medium */}
											<div className="min-w-0 flex-1">
												<div className="h-5 w-28 animate-pulse rounded bg-bg-tertiary" />
											</div>
											{/* Price - font-bold tabular-nums */}
											<div className="h-5 w-10 shrink-0 animate-pulse rounded bg-bg-tertiary" />
											{/* Buy + Sell buttons */}
											<div className="flex shrink-0 gap-1">
												<div className="h-[30px] w-[42px] animate-pulse rounded-lg bg-breakout-brand/30" />
												<div className="h-[30px] w-[42px] animate-pulse rounded-lg border border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/10" />
											</div>
										</div>
									))}
								</div>
							</div>

							{/* Chart skeleton - unboxed, matches actual PriceChart structure */}
							<div className="w-full">
								{/* Chart legend skeleton */}
								<div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 sm:gap-x-5">
									{[{ color: "#00D952" }, { color: "#ef4444" }].map(({ color }) => (
										<div className="flex items-center gap-1.5 sm:gap-2" key={color}>
											<span
												className="size-2.5 shrink-0 rounded-full sm:size-3"
												style={{ backgroundColor: color }}
											/>
											<div className="h-4 w-12 animate-pulse rounded bg-bg-tertiary sm:w-16" />
											<div className="h-5 w-10 animate-pulse rounded bg-bg-tertiary sm:h-7 sm:w-12" />
										</div>
									))}
								</div>
								{/* Chart area - matches actual: relative aspect-[320/200] */}
								<div className="relative aspect-[320/200]">
									<svg
										aria-hidden="true"
										className="absolute inset-0 size-full"
										preserveAspectRatio="xMidYMid meet"
										viewBox="0 0 320 200"
									>
										{/* Transparent background - no card wrapper in actual */}
										<rect fill="transparent" height={200} rx={0} width={320} />
										{/* Grid lines at 0%, 25%, 50%, 75%, 100% - matches chart padding */}
										{[0, 0.25, 0.5, 0.75, 1].map(tick => {
											const y = 16 + (1 - tick) * 160
											return (
												<g key={tick}>
													<line
														className="stroke-border-subtle"
														strokeWidth={0.5}
														x1={12}
														x2={288}
														y1={y}
														y2={y}
													/>
													<text
														className="fill-text-secondary text-[9px]"
														dominantBaseline="middle"
														textAnchor="end"
														x={316}
														y={y}
													>
														{(tick * 100).toFixed(0)}%
													</text>
												</g>
											)
										})}
									</svg>
								</div>
							</div>

							{/* Description + resource links skeleton */}
							<div>
								<div className="space-y-2">
									<div className="h-4 w-full animate-pulse rounded bg-bg-tertiary" />
									<div className="h-4 w-3/4 animate-pulse rounded bg-bg-tertiary" />
								</div>
								<div className="mt-2 flex items-center gap-3">
									<div className="h-3 w-16 animate-pulse rounded bg-bg-tertiary" />
									<div className="h-3 w-12 animate-pulse rounded bg-bg-tertiary" />
								</div>
							</div>

							{/* Tabs skeleton for Tweets / Top Holders / Activity */}
							<div className="border-border-subtle border-b">
								<div className="mt-2 flex gap-2">
									{[
										{ label: "Tweets", active: true },
										{ label: "Top Holders", active: false },
										{ label: "Activity", active: false },
									].map(tab => (
										<div
											className={`px-4 py-3 font-medium text-sm ${
												tab.active
													? "border-breakout-brand border-b-2 text-text-primary"
													: "text-text-muted"
											}`}
											key={tab.label}
										>
											{tab.label}
										</div>
									))}
								</div>
							</div>

							{/* Top holders skeleton - matches actual TopHolders structure */}
							<div className="w-full overflow-hidden">
								{/* Mobile: dropdown selector skeleton */}
								<div className="mb-4 sm:hidden">
									<div className="h-10 w-48 animate-pulse rounded-xl bg-bg-tertiary" />
								</div>
								{/* Mobile: single column */}
								<div className="sm:hidden">
									<div className="mb-2 flex items-center px-2">
										<div className="h-4 w-24 animate-pulse rounded bg-bg-tertiary" />
										<div className="ml-auto h-3 w-10 animate-pulse rounded bg-bg-tertiary" />
									</div>
									<div className="space-y-0.5">
										{[0, 1, 2, 3, 4].map(i => (
											<div className="flex items-center gap-2 px-2 py-1.5" key={i}>
												<div className="size-7 animate-pulse rounded-full bg-bg-tertiary" />
												<div className="h-4 max-w-[120px] flex-1 animate-pulse rounded bg-bg-tertiary" />
												<div className="ml-auto h-4 w-10 animate-pulse rounded bg-bg-tertiary" />
											</div>
										))}
									</div>
								</div>
								{/* Desktop: two-column grid for binary markets */}
								<div className="hidden gap-6 sm:grid sm:grid-cols-2">
									{[0, 1].map(idx => (
										<div key={idx}>
											<div className="mb-2 flex items-center px-2">
												<div className="h-4 w-24 animate-pulse rounded bg-bg-tertiary" />
												<div className="ml-auto h-3 w-10 animate-pulse rounded bg-bg-tertiary" />
											</div>
											<div className="space-y-0.5">
												{[0, 1, 2, 3, 4].map(i => (
													<div className="flex items-center gap-2 px-2 py-1.5" key={`${idx}-${i}`}>
														<div className="size-7 animate-pulse rounded-full bg-bg-tertiary" />
														<div className="h-4 max-w-[120px] flex-1 animate-pulse rounded bg-bg-tertiary" />
														<div className="ml-auto h-4 w-10 animate-pulse rounded bg-bg-tertiary" />
													</div>
												))}
											</div>
										</div>
									))}
								</div>
							</div>
						</div>

						{/* Desktop trading panel skeleton - matches TradingPanel structure */}
						<aside className="hidden self-start lg:order-2 lg:block">
							<div className="sticky top-24">
								<div className="rounded-3xl border border-border-subtle bg-bg-card p-6 shadow-glow">
									<div className="space-y-5">
										{/* Buy/Sell Toggle - matches TradeModeToggle */}
										<fieldset className="flex rounded-xl border-0 bg-bg-tertiary p-1 ring-1 ring-border-subtle">
											<div className="min-h-[44px] flex-1 rounded-lg bg-bg-card py-2.5 text-center font-semibold text-base text-text-primary shadow-sm ring-1 ring-border-subtle">
												Buy
											</div>
											<div className="min-h-[44px] flex-1 rounded-lg py-2.5 text-center font-semibold text-base text-text-muted">
												Sell
											</div>
										</fieldset>
										{/* Outcome buttons - matches OutcomeSelector */}
										<fieldset className="space-y-2 border-0">
											<legend className="mb-2 text-sm text-text-secondary">Choose outcome</legend>
											<div className="grid gap-2">
												{[
													{ id: "yes", selected: true },
													{ id: "no", selected: false },
												].map(style => (
													<div
														className={cn(
															"flex min-h-[52px] items-center justify-between rounded-xl border-2 px-3 py-2.5 sm:px-4 sm:py-3",
															style.selected
																? "border-green-500 bg-green-500/10"
																: "border-border bg-bg-card",
														)}
														key={style.id}
													>
														<div className="flex items-center gap-3">
															<div className="relative size-8 shrink-0">
																<div
																	className={cn(
																		"size-8 animate-pulse rounded-lg",
																		style.selected ? "bg-green-500/20" : "bg-red-500/20",
																	)}
																/>
																<div
																	className={cn(
																		"absolute -right-1 -bottom-1 size-4 rounded-full",
																		style.selected ? "bg-green-500" : "bg-red-500",
																	)}
																/>
															</div>
															<div className="h-6 w-24 animate-pulse rounded bg-bg-tertiary" />
														</div>
														<div className="h-7 w-10 animate-pulse rounded bg-bg-tertiary" />
													</div>
												))}
											</div>
										</fieldset>
										{/* Amount input - matches AmountInput */}
										<div>
											<label className="text-sm text-text-secondary" htmlFor="skeleton-amount">
												Amount (USDC)
											</label>
											<div className="relative mt-2">
												<span className="absolute top-1/2 left-4 -translate-y-1/2 font-semibold text-text-muted text-xl">
													$
												</span>
												<div className="h-[50px] w-full rounded-xl border border-border bg-bg-card" />
											</div>
											{/* Quick amount buttons - matches actual responsive sizing */}
											<div className="mt-3 flex gap-2">
												{["25%", "50%", "75%", "Max"].map(label => (
													<div
														className="min-h-[44px] flex-1 rounded-lg border border-border bg-bg-card py-2.5 text-center text-sm text-text-muted sm:py-3 sm:text-base"
														key={label}
													>
														{label}
													</div>
												))}
											</div>
										</div>
										{/* Payout preview - matches actual right-aligned inline text */}
										<div className="mt-2 text-right">
											<div className="ml-auto h-7 w-40 animate-pulse rounded bg-bg-tertiary" />
										</div>
										{/* Action button - matches Button size="lg" */}
										<button
											className="min-h-[48px] w-full rounded-xl bg-breakout-brand py-3 font-semibold text-black opacity-50"
											disabled
											type="button"
										>
											Buy Long
										</button>
									</div>
								</div>
							</div>
						</aside>
					</div>
				</PageContainer>
			</div>
		)
	}

	// Show "not found" only when we've definitively fetched and found nothing
	if (isNotFound) {
		return (
			<div className="min-h-[60vh] bg-bg-primary px-4 py-16 text-text-primary sm:px-6 lg:px-8">
				<div className="mx-auto max-w-3xl rounded-2xl border border-border-subtle bg-bg-card p-8 text-center shadow-sm">
					<h2 className="text-balance font-semibold text-lg">Market not found</h2>
					<p className="mt-2 text-pretty text-base text-text-secondary">
						This market doesn&apos;t exist or may have been removed.
					</p>
					<Link
						className="mt-6 inline-flex items-center gap-2 rounded-full bg-text-primary px-4 py-2 font-semibold text-bg-primary"
						to="/"
					>
						<ArrowLeft aria-hidden="true" className="size-4" />
						Back to markets
					</Link>
				</div>
			</div>
		)
	}

	return (
		<div className="overflow-x-hidden bg-bg-primary text-text-primary" data-testid="market-detail">
			<PageContainer className="pt-5 pb-8 sm:pt-8">
				<div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-12">
					<section className="min-w-0 space-y-8 lg:order-1">
						<motion.div
							animate={{ opacity: 1, y: 0 }}
							className="px-1"
							initial={{ opacity: 0, y: 6 }}
							transition={{ duration: 0.25, ease: "easeOut" }}
						>
							{/* Header: avatar + name + timer badge */}
							{isFallFromGrace ? (
								<>
									{/* FFG: Simplified header with time badge only */}
									<div className="flex items-center justify-end">
										<CountdownBadge
											openTimeMs={marketOpenTimeMs}
											size="sm"
											targetMs={marketCloseTimeMs}
										/>
									</div>
									{/* FFG: Crown icon + question on same line */}
									<div className="mt-4 flex items-center gap-3 sm:gap-4">
										<img
											alt=""
											aria-hidden="true"
											className="h-8 w-10 shrink-0 sm:h-10 sm:w-12"
											src="/fallfromgrace.png"
										/>
										<h1 className="text-balance font-bold font-display text-2xl text-text-primary leading-tight sm:text-3xl">
											{marketTitle}
										</h1>
									</div>
								</>
							) : (
								<>
									{/* Binary markets: Standard header with avatar + name + timer */}
									<div className="flex items-center gap-3 sm:gap-4">
										<SimpleAvatar
											alt={`${marketName} market avatar`}
											className="size-12 shrink-0 rounded-xl shadow-lg sm:size-14 sm:rounded-2xl"
											size="lg"
											src={avatarUrl}
											style={{ viewTransitionName: `market-avatar-${marketId}` }}
											variant="square"
										/>
										{/* Title */}
										<div className="flex min-w-0 flex-1 items-center gap-2">
											<h1 className="truncate font-bold text-lg text-text-primary leading-tight sm:text-2xl lg:text-3xl">
												{marketName}
											</h1>
											{(market?.handle || routeState?.handle) && (
												<a
													className="inline-flex shrink-0 items-center gap-1 text-text-muted transition-colors hover:text-text-primary"
													href={`https://x.com/${market?.handle || routeState?.handle}`}
													rel="noopener noreferrer"
													target="_blank"
												>
													<span className="sr-only">View {marketName} on X (opens in new tab)</span>
													<XLogo aria-hidden="true" className="size-4" />
												</a>
											)}
										</div>
										{/* Time remaining badge */}
										<CountdownBadge
											openTimeMs={marketOpenTimeMs}
											size="sm"
											targetMs={marketCloseTimeMs}
										/>
									</div>
									{/* Market question */}
									<h1 className="mt-6 text-balance font-bold font-display text-2xl text-text-primary leading-tight sm:text-3xl">
										{marketTitle}
									</h1>
								</>
							)}

							{/* Stats row - unboxed and clean */}
							{/* Always show all stats to prevent layout shift - use inline skeletons for loading */}
							<div className="mt-6 flex items-center gap-x-5 text-base sm:gap-x-8">
								{/* Rank - first for binary markets (not custom questions) */}
								{!isFallFromGrace && !isCustomQuestion && (
									<div className="flex flex-col gap-0.5">
										<span className="font-semibold text-text-muted text-xs uppercase">Rank</span>
										<span className="font-bold font-mono text-lg text-text-primary">
											{startRank ? (
												`#${startRank}`
											) : (
												<span className="inline-block h-7 w-8 animate-pulse rounded bg-bg-tertiary" />
											)}
										</span>
									</div>
								)}
								{/* Chance / Projected leader for FFG */}
								<div className="flex flex-col gap-0.5">
									<span className="font-semibold text-text-muted text-xs uppercase">
										{isFallFromGrace ? "Projected" : "Chance"}
									</span>
									{isFallFromGrace ? (
										prices && leadingBucket ? (
											<span className="truncate font-bold text-lg text-text-positive">
												{leadingBucket.label.replace(/ falls most$/, "")}
											</span>
										) : (
											<span className="inline-block h-7 w-24 animate-pulse rounded bg-bg-tertiary" />
										)
									) : (
										<NumberTicker
											className="font-bold text-lg text-text-positive"
											format={v => `${Math.round(v * 100)}%`}
											value={probabilities[0] ?? 0}
										/>
									)}
								</div>
								{/* Mindshare - only for binary markets (not custom questions) */}
								{!isFallFromGrace && !isCustomQuestion && (
									<div className="flex flex-col gap-0.5">
										<span className="font-semibold text-text-muted text-xs uppercase">
											Mindshare
										</span>
										<span className="font-bold text-lg">
											{mindsharePct != null ? (
												<NumberTicker
													className="font-bold text-lg text-text-primary"
													format={v => `${v.toFixed(2)}%`}
													value={mindsharePct}
												/>
											) : (
												<span className="inline-block h-7 w-16 animate-pulse rounded bg-bg-tertiary" />
											)}
										</span>
									</div>
								)}
								{/* Volume */}
								<div className="flex flex-col gap-0.5">
									<span className="font-semibold text-text-muted text-xs uppercase">Vol</span>
									<span className="font-bold font-mono text-lg text-text-primary">
										{volumeUsd !== null ? (
											<NumberTicker
												className="font-bold text-lg text-text-primary"
												format={formatCompactUsd}
												value={volumeUsd}
											/>
										) : (
											<span className="inline-block h-7 w-14 animate-pulse rounded bg-bg-tertiary" />
										)}
									</span>
								</div>
								{/* Liquidity */}
								<div className="flex flex-col gap-0.5">
									<span className="font-semibold text-text-muted text-xs uppercase">Liq.</span>
									<span className="font-bold font-mono text-lg text-text-primary">
										{liquidityUsd !== null ? (
											<NumberTicker
												className="font-bold text-lg text-text-primary"
												format={formatCompactUsd}
												value={liquidityUsd}
											/>
										) : (
											<span className="inline-block h-7 w-14 animate-pulse rounded bg-bg-tertiary" />
										)}
									</span>
								</div>
							</div>
						</motion.div>

						{/* Mobile Outcome Rows - compact single-line layout */}
						<div className="lg:hidden">
							<div className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
								{buckets.map(bucket => (
									<OutcomeRow
										bucket={bucket}
										isFallFromGrace={isFallFromGrace}
										isSubmitting={isSubmitting}
										isTradingEnabled={isTradingEnabled}
										key={bucket.id}
										onBuy={handleMobileBuy}
										onSell={handleMobileSell}
										userShares={userSharesRecord[bucket.id] ?? 0}
										variant="compact"
									/>
								))}
							</div>
						</div>

						{/* Price Chart - Always visible */}
						<PriceChart
							buckets={buckets}
							isLive={!isMarketEnded}
							marketAddress={marketAddress}
							marketCloseTimeMs={marketCloseTimeMs ?? undefined}
							marketOpenTimeMs={marketOpenTimeMs ?? undefined}
							probabilities={probabilities}
						/>

						{/* Market description + resource links */}
						<div>
							{isFallFromGrace ? (
								<MarketDescription
									closeTimeMs={marketCloseTimeMs}
									customDescription={typedMarket?.customDescription}
									marketType="fall_from_grace"
								/>
							) : (
								<MarketDescription
									closeTimeMs={marketCloseTimeMs}
									creatorName={marketName}
									customDescription={typedMarket?.customDescription}
									marketType="binary"
								/>
							)}
							<nav
								aria-label="Market resources"
								className="mt-2 flex items-center gap-3 text-text-muted text-xs"
							>
								{marketAddress && (
									<a
										aria-label="View market contract on block explorer"
										className="inline-flex items-center gap-1 rounded transition-colors hover:text-text-secondary hover:underline"
										href={`${breakoutChain.blockExplorers?.default?.url ?? "https://basescan.org"}/address/${marketAddress}`}
										rel="noopener noreferrer"
										target="_blank"
									>
										<ExternalLink aria-hidden="true" className="size-3" />
										Onchain
									</a>
								)}
								{weekDetails?.startSnapshot?.ipfsCid && (
									<>
										<span aria-hidden="true" className="text-text-muted">
											·
										</span>
										<a
											aria-label="View start snapshot on IPFS"
											className="inline-flex items-center gap-1 rounded transition-colors hover:text-text-secondary hover:underline"
											href={`https://gateway.pinata.cloud/ipfs/${weekDetails.startSnapshot.ipfsCid}`}
											rel="noopener noreferrer"
											target="_blank"
										>
											<ExternalLink aria-hidden="true" className="size-3" />
											Start
										</a>
									</>
								)}
								{isMarketEnded && weekDetails?.endSnapshot?.ipfsCid && (
									<>
										<span aria-hidden="true" className="text-text-muted">
											·
										</span>
										<a
											aria-label="View end snapshot on IPFS"
											className="inline-flex items-center gap-1 rounded transition-colors hover:text-text-secondary hover:underline"
											href={`https://gateway.pinata.cloud/ipfs/${weekDetails.endSnapshot.ipfsCid}`}
											rel="noopener noreferrer"
											target="_blank"
										>
											<ExternalLink aria-hidden="true" className="size-3" />
											End
										</a>
									</>
								)}
							</nav>
						</div>

						{/* Tab Navigation for Tweets/Holders/Activity */}
						<div className="border-border-subtle border-b">
							<AnimatedTabs
								activeTab={activeTab}
								className="mt-2"
								onTabChange={id => setActiveTab(id as MarketTab)}
								tabs={[
									{ id: "tweets", label: "Tweets" },
									{ id: "holders", label: "Top Holders" },
									{ id: "activity", label: "Activity" },
								]}
							/>
						</div>

						{/* Tab Content */}
						{activeTab === "tweets" && (
							<div
								aria-labelledby="tab-tweets"
								className="min-h-[400px]"
								id="tabpanel-tweets"
								role="tabpanel"
							>
								<TweetsFeed
									entityKey={market?.entityKey ?? routeState?.handle?.toLowerCase() ?? ""}
									sinceMs={marketOpenTimeMs ?? 0}
								/>
							</div>
						)}

						{activeTab === "holders" && (
							<div
								aria-labelledby="tab-holders"
								className="min-h-[400px]"
								id="tabpanel-holders"
								role="tabpanel"
							>
								<TopHolders
									buckets={buckets}
									isFallFromGrace={isFallFromGrace}
									marketAddress={marketAddress}
								/>
							</div>
						)}

						{activeTab === "activity" && (
							<div
								aria-labelledby="tab-activity"
								className="min-h-[400px]"
								id="tabpanel-activity"
								role="tabpanel"
							>
								<ActivityFeed buckets={buckets} marketAddress={marketAddress} />
							</div>
						)}
					</section>

					{/* Desktop Sticky Trading Panel */}
					<aside className="hidden self-start lg:order-2 lg:block">
						<div className="sticky top-24">
							<div
								className="rounded-3xl border border-border-subtle bg-bg-card p-6 shadow-glow"
								data-testid="market-trade-panel"
							>
								<TradingPanel
									amount={amount}
									buckets={buckets}
									estimatedShares={estimatedShares}
									isAuthenticated={isAuthenticated}
									isFallFromGrace={isFallFromGrace}
									isMarketEnded={isMarketEnded}
									isSubmitting={isSubmitting}
									onAddCash={handleAddCash}
									onConfirm={handleDesktopConfirm}
									onLogin={login}
									onSelectBucket={bucket => setSelectedBucketId(bucket.id)}
									onSetAmount={setAmount}
									onSetTradeMode={setTradeSide}
									openTimeMs={marketOpenTimeMs}
									selectedBucketId={selectedBucketId}
									showAddCash={showAddCash}
									statusLabel={getStatusLabel()}
									tradeMode={tradeSide}
									userBalanceRaw={userBalanceRaw}
									userShares={userSharesRecord}
								/>
							</div>
						</div>
					</aside>
				</div>
			</PageContainer>

			<MarketSheet
				buckets={buckets}
				creator={creator}
				initialAmount={amount}
				initialBucketId={selectedBucketId}
				initialTradeMode={tradeSide}
				isLoading={isLoadingPrices}
				isMarketEnded={isMarketEnded}
				isOpen={isSheetOpen}
				marketTitle={marketTitle}
				onAddCash={handleAddCash}
				onBuy={handleBuy}
				onClose={() => setIsSheetOpen(false)}
				onSell={handleSell}
				showAddCash={showAddCash}
				userBalanceRaw={userBalanceRaw}
				userShares={userSharesRecord}
			/>

			{/* Share trade modal - shown after successful buy */}
			<ShareTradeModal
				avatarUrl={avatarUrl}
				direction={lastTradedDirection}
				handle={market?.handle ?? routeState?.handle}
				isOpen={shareModalOpen}
				marketName={marketName}
				marketTitle={marketTitle}
				marketUrl={typeof window !== "undefined" ? window.location.href : ""}
				onClose={() => setShareModalOpen(false)}
				outcomeLabel={lastTradedOutcomeLabel}
				probability={probabilities[0]}
			/>
		</div>
	)
}

function shortenKey(value: string): string {
	if (value.length <= 12) {
		return value
	}
	return `${value.slice(0, 6)}...${value.slice(-4)}`
}

function hashString(value: string | undefined | null): number {
	if (!value) {
		return 1
	}
	let hash = 0
	for (let i = 0; i < value.length; i += 1) {
		hash = (hash << 5) - hash + value.charCodeAt(i)
		hash |= 0
	}
	return Math.abs(hash)
}

function createSeededProbabilities(seed: string, count: number): number[] {
	let state = hashString(seed) || 1
	const values = Array.from({ length: count }, () => {
		state = (state * 1664525 + 1013904223) % 4_294_967_296
		return (state % 70) + 30
	})
	const total = values.reduce((sum, value) => sum + value, 0)
	return values.map(value => value / total)
}
